import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { classesApi } from '../../api/classes.api';

export interface SectionMonitorModalProps {
  sectionUuid: string;
  /** "Class 8 - A", for the title before the read arrives. */
  label: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Chooses, changes or removes one section's class monitor.
 *
 * The list is the section's own roster, by roll number — the server refuses
 * anybody else. Mounted only once a section is chosen, so each open reads the
 * section fresh rather than showing last time's students.
 */
export function SectionMonitorModal({ sectionUuid, label, onClose, onSaved }: SectionMonitorModalProps) {
  const { data, isLoading, error, refetch } = useApiQuery(() => classesApi.sectionMonitor(sectionUuid), [sectionUuid]);
  const [chosen, setChosen] = useState('');
  const [saving, setSaving] = useState<'save' | 'remove' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setChosen(data?.monitor?.uuid ?? '');
  }, [data]);

  async function save(studentUuid: string | null) {
    setSaving(studentUuid ? 'save' : 'remove');
    setSaveError(null);
    try {
      const result = await classesApi.setSectionMonitor(sectionUuid, studentUuid);
      toast.success(
        result.monitor ? 'Class monitor saved' : 'Class monitor removed',
        result.monitor
          ? `${result.monitor.fullName} is the class monitor of ${result.section.displayName}.`
          : `${result.section.displayName} has no class monitor now.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      setSaveError(caught instanceof ApiRequestError ? caught.message : 'Could not save. Please try again.');
    } finally {
      setSaving(null);
    }
  }

  const current = data?.monitor ?? null;
  const options = (data?.candidates ?? []).map((student) => ({
    value: student.uuid,
    label: student.rollNumber ? `${student.rollNumber} · ${student.fullName}` : student.fullName,
  }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Class monitor — ${data?.section.displayName ?? label}`}
      description="One student of this section who helps the class teacher run the class."
      size="sm"
      footer={
        <>
          {current && (
            <Button variant="secondary" onClick={() => void save(null)} isLoading={saving === 'remove'} disabled={saving !== null}>
              Remove monitor
            </Button>
          )}
          <Button
            onClick={() => void save(chosen)}
            isLoading={saving === 'save'}
            disabled={saving !== null || !chosen || chosen === current?.uuid}
          >
            {current ? 'Change monitor' : 'Assign monitor'}
          </Button>
        </>
      }
    >
      {isLoading && !data && <SkeletonLines count={3} />}

      {error && <QueryError title="Could not load this section's students" error={error} onRetry={refetch} />}

      {data && data.candidates.length === 0 && (
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No students in this section"
          description="Place students in this section first, then choose one of them as class monitor."
        />
      )}

      {data && data.candidates.length > 0 && (
        <div className="flex flex-col gap-4">
          {saveError && (
            <Alert tone="critical" title="Could not save the class monitor">
              {saveError}
            </Alert>
          )}
          <Select
            label="Student"
            placeholder="Choose a student"
            options={options}
            value={chosen}
            hint={current ? `Currently ${current.fullName}.` : 'Listed by roll number.'}
            onChange={(event) => setChosen(event.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
